import type { IntlShape } from "react-intl";
import type { RoomDetail } from "../../domain/HotelsScreen/HotelsContext";
import {
  MAX_ADULTS,
  MAX_CHILDREN,
  MAX_CHILDREN_IN_ROOM,
  MAX_GUESTS,
  MAX_GUESTS_IN_ROOM,
  MAX_ROOMS,
} from "../../domain/HotelsScreen/HotelSearch/constants";
import constantsMessages from "../../domain/HotelsScreen/HotelSearch/constants.messages";

type WarningType = "error" | "warning";

export type HotelRoomFormWarning = {
  active: boolean;
  message: string;
  type: WarningType;
};

export type BookingWarnings = {
  childAgeRequired: HotelRoomFormWarning;
  roomLimitWarning: HotelRoomFormWarning;
  totalGuestLimitWarning: HotelRoomFormWarning;
  adultGuestLimitWarning: HotelRoomFormWarning;
  childrenGuestLimitWarning: HotelRoomFormWarning;
};

export type RoomWarnings = {
  maxGuestsInRoomWarning?: HotelRoomFormWarning;
  maxChildrenInRoomWarning?: HotelRoomFormWarning;
};

export function useHotelRoomValidation({
  freshFormData,
  intl,
}: {
  freshFormData: RoomDetail[];
  intl: IntlShape;
}) {
  const bookingWarnings = getBookingDetailsWarnings(freshFormData, intl);

  // Check if there are any active error-type warnings
  const hasErrors = Object.values(bookingWarnings).some(
    (warning) => warning.active && warning.type === "error",
  );

  // Helper to check if add room should be disabled
  const shouldDisableAddRoom =
    bookingWarnings.roomLimitWarning.active ||
    bookingWarnings.adultGuestLimitWarning.active ||
    bookingWarnings.totalGuestLimitWarning.active;

  return {
    bookingWarnings,
    hasErrors,
    shouldDisableAddRoom,
  };
}

function getBookingDetailsWarnings(
  freshFormData: RoomDetail[],
  intl: IntlShape,
) {
  const totalAdultsInBooking = freshFormData.reduce(
    (acc, curr) => acc + curr.adults,
    0,
  );
  const totalChildrenInBooking = freshFormData.reduce(
    (acc, curr) => acc + curr.children.length,
    0,
  );
  const totalGuestsInBooking = totalAdultsInBooking + totalChildrenInBooking;
  const maxRoomsWarning = freshFormData.length >= MAX_ROOMS;
  const isChildWithoutAge = freshFormData.some((room) =>
    room.children.some((child) => child.age === undefined),
  );

  const bookingWarnings: BookingWarnings = {
    childAgeRequired: {
      active: isChildWithoutAge,
      message: intl.formatMessage(constantsMessages.childAgeNeededReason),
      type: "error",
    },
    roomLimitWarning: {
      active: maxRoomsWarning,
      message: intl.formatMessage(constantsMessages.maxRoomsMsg, {
        MAX_ROOMS,
      }),
      type: "warning",
    },
    totalGuestLimitWarning: {
      active: totalGuestsInBooking >= MAX_GUESTS,
      message: intl.formatMessage(constantsMessages.maxGuestsMsg, {
        MAX_GUESTS,
      }),
      type: "warning",
    },
    adultGuestLimitWarning: {
      active: totalAdultsInBooking >= MAX_ADULTS,
      message: intl.formatMessage(constantsMessages.maxAdultsInBookingMsg, {
        MAX_ADULTS,
      }),
      type: "warning",
    },
    childrenGuestLimitWarning: {
      active: totalChildrenInBooking >= MAX_CHILDREN,
      message: intl.formatMessage(constantsMessages.maxChildrenInBookingMsg, {
        MAX_CHILDREN,
      }),
      type: "warning",
    },
  };

  return bookingWarnings;
}

export function getRoomDetailsWarnings(room: RoomDetail, intl: IntlShape) {
  const totalChildrenInRoom = room.children.length;
  const totalGuestsInRoom = room.adults + totalChildrenInRoom;

  const roomWarnings: RoomWarnings = {
    maxGuestsInRoomWarning: {
      active: totalGuestsInRoom >= MAX_GUESTS_IN_ROOM,
      message: intl.formatMessage(constantsMessages.maxGuestsInRoomMsg, {
        MAX_GUESTS_IN_ROOM,
      }),
      type: "warning",
    },
    maxChildrenInRoomWarning: {
      active: totalChildrenInRoom >= MAX_CHILDREN_IN_ROOM,
      message: intl.formatMessage(constantsMessages.maxChildrenInRoomMsg, {
        MAX_CHILDREN_IN_ROOM,
      }),
      type: "warning",
    },
  };
  return roomWarnings;
}
